import { AppRegistry } from 'react-native';

import DeviceProps from '@magnus/react-native-device-props';
import 'firebase/compat/analytics';
import firebase from 'firebase/compat/app';
import queryString from 'query-string';
import 'firebase/compat/auth';
import {
	FIREBASE_API_KEY,
	FIREBASE_APP_ID,
	FIREBASE_AUTH_DOMAIN,
	FIREBASE_MEASUREMENT_ID,
	FIREBASE_MESSAGING_SENDER_ID,
	FIREBASE_PROJECT_ID,
	FIREBASE_STORAGE_BUCKET,
} from 'react-native-dotenv';
import { configureReanimatedLogger, ReanimatedLogLevel } from 'react-native-reanimated';

import './web/polyfills/dotlottie-wasm-config';
import { name as appName } from './app.json';
import geistBold from './assets/fonts/Geist-Bold.ttf';
import geistRegular from './assets/fonts/Geist-Regular.ttf';
import geistSemiBold from './assets/fonts/Geist-SemiBold.ttf';
import martianGroteskExtraBold from './assets/fonts/MartianGrotesk-WdxBd.ttf';
import packageJson from './package.json';

const query = queryString.parse(window.location.search);

if (query.idfm) {
	window.localStorage.setItem('@RNMV/IDFM', String(query.idfm));
}

const FIREBASE_CONFIG = {
	apiKey: FIREBASE_API_KEY,
	appId: FIREBASE_APP_ID,
	authDomain: FIREBASE_AUTH_DOMAIN,
	measurementId: FIREBASE_MEASUREMENT_ID,
	messagingSenderId: FIREBASE_MESSAGING_SENDER_ID,
	projectId: FIREBASE_PROJECT_ID,
	storageBucket: FIREBASE_STORAGE_BUCKET,
};

const initFirebaseMessaging = async () => {
	if (!('serviceWorker' in navigator)) {
		console.warn('[Firebase Messaging] Service Worker not available (HTTP context), skipping');

		return;
	}

	try {
		const { getMessaging, isSupported } = await import('firebase/messaging');
		const app = firebase.app();
		const supported = await isSupported();

		if (supported) {
			getMessaging(app);
		}
	} catch (error) {
		console.warn('[Firebase Messaging] Initialization failed:', error);
	}
};

if (!firebase.apps.length) {
	firebase.initializeApp(FIREBASE_CONFIG);
} else {
	firebase.app();
}

void initFirebaseMessaging();

try {
	firebase.analytics();

	console.log('Firebase Analytics initialized successfully');
} catch (error) {
	console.warn('Firebase Analytics initialization failed:', error);
}

const martianGroteskExtraBoldFontStyles = `@font-face {
  src: url(${martianGroteskExtraBold});
  font-family: 'MartianGrotesk-WdxBd';
  font-weight: 700;
  font-style: normal;
	font-display: swap;
}`;

const geistBoldFontStyles = `@font-face {
  src: url(${geistBold});
  font-family: 'Geist-Bold';
  font-weight: 700;
  font-style: normal;
	font-display: swap;
}`;
const geistRegularFontStyles = `@font-face {
  src: url(${geistRegular});
  font-family: 'Geist-Regular';
  font-weight: 400;
  font-style: normal;
	font-display: swap;
}`;
const geistSemiBoldFontStyles = `@font-face {
  src: url(${geistSemiBold});
  font-family: 'Geist-SemiBold';
  font-weight: 600;
  font-style: normal;
	font-display: swap;
}`;

const style = document.createElement('style');

style.appendChild(document.createTextNode(martianGroteskExtraBoldFontStyles));
style.appendChild(document.createTextNode(geistBoldFontStyles));
style.appendChild(document.createTextNode(geistRegularFontStyles));
style.appendChild(document.createTextNode(geistSemiBoldFontStyles));

document.head.appendChild(style);

globalThis.RNFB_SILENCE_MODULAR_DEPRECATION_WARNINGS = true;

configureReanimatedLogger({
	level: ReanimatedLogLevel.warn,
	strict: false, // Enable to get more detailed logs
});

const appVersion = packageJson.version;

DeviceProps.setAppVersion(appVersion);

configureReanimatedLogger({
	level: ReanimatedLogLevel.warn,
	strict: true,
});

const initApp = async () => {
	const App = (await import('./src/index.tsx')).default;

	const rootElement = document.getElementById('root');

	if (!rootElement) {
		throw new Error('Root element #root not found');
	}

	AppRegistry.registerComponent(appName, () => App);

	AppRegistry.runApplication(appName, {
		initialProps: {},
		// @ts-expect-error react-native-web takes a DOM element as rootTag, RN types declare an opaque symbol
		rootTag: rootElement,
	});
};

void initApp();
